import { ChangeEvent, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { CustomAccordion, CustomAlert, CustomButton, CustomSwitch } from '../../../../shared/uiComponents';
import { ELC_FUNDER_ID } from '../../../../redux/API/constants';
import { Tab, TabsContainer } from '../../../../shared/uiComponents/Tabs';
import Text from '../../../../shared/uiComponents/Text';
import { BACKGROUND } from '../../../../shared/Helpers/colors';
import { ProgramsContent } from './SkillAreas/Components/SkillAccordionContent';
import { ProgramsHeader } from './SkillAreas/Components/SkillAccordionHeader';
import { AddSkillContent } from './SkillAreas/Components/AddSkillContent';
import { AdminTypes } from '../../../../components/Menu';
import UploadTargets from './Actions/uploadTargets';

import API from '../../../../redux/API/Main/API';
import { ClientFunderProperties } from '../../../../redux/API/ClientAPIHelpers/funding';
import { DispatchProperties, useSelector } from '../../../../redux/store';
import { clearSkillAreas, getSkillAreas } from '../../../../redux/State/clientSlice/skillAreaSlice';
import { Plus } from '../../../../shared/Assets/svgIcons';
import { PlusIcon } from '../../../../shared/Assets/svg';
import { useScreenWidth } from '../../../../shared/Helpers/hooks';

// Funder name of the ELC day-center funding stream (matches the seeded Funder.Name).
const ELC_FUNDER_NAME = 'ELC';

interface SkillAcquisitionProps {
	mobileHeaderButton?: boolean;
	mobileUploadButton?: boolean;
}

const SkillAcquisition = ({ mobileHeaderButton, mobileUploadButton }: SkillAcquisitionProps = {}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params, setParams] = useSearchParams();

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const role = useSelector((state) => state.account.role);
	const skillAreas = useSelector((state) => state.skillArea.skillAreas);
	const [includeArchived, setIncludeArchived] = useState<boolean>(false);
	const [addSkillAreaOpen, setAddSkillAreaOpen] = useState<boolean>(false);

	// Funder tabs: null = Insurance + DOE (shared tree); elcFunderId = ELC (separate tree).
	// The ELC tab only appears for clients linked to the ELC funder.
	const [elcFunderId, setElcFunderId] = useState<string | null>(null);
	const activeFunderId = params.get('skillFunderId') || null;

	const isBT = role?.section.id === AdminTypes.bt;

	useEffect(() => {
		if (!clientId) return undefined;

		let cancelled = false;

		API.ClientAPI.Funding.getClientFunders(clientId).then((response) => {
			if (cancelled || response?.error) return;
			const funders = response as ClientFunderProperties[];
			const elc = funders?.find((f) => f.funderName === ELC_FUNDER_NAME);
			setElcFunderId(elc?.funderId ?? null);
		});

		return () => {
			cancelled = true;
		};
	}, [clientId]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getSkillAreas({ clientId, includeArchived, funderId: activeFunderId }));
	}, [clientId, includeArchived, activeFunderId, dispatch]);

	const selectFunderTab = (funderId: string | null) => {
		if (funderId === activeFunderId) return;

		// Reset selection so the accordion re-selects the first skill area in the new stream.
		dispatch(clearSkillAreas());

		if (funderId) {
			params.set('skillFunderId', funderId);
		} else {
			params.delete('skillFunderId');
		}
		setParams(params, { replace: true });
	};

	const showArchivedSkillAreasHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setIncludeArchived(checked);
	};

	const onCloseAddSkillArea = () => setAddSkillAreaOpen(false);

	// If this is just rendering the mobile upload button
	if (mobileUploadButton) {
		return isBT ? null : <UploadTargets funderId={activeFunderId} />;
	}

	// If this is just rendering the mobile header button
	if (mobileHeaderButton) {
		return isBT ? null : (
			<CustomButton
				iconOnly={<PlusIcon />}
				buttonType="primary"
				sizeType="sm"
				onClick={() => setAddSkillAreaOpen(true)}
			/>
		);
	}

	const isElcActive = activeFunderId?.toLowerCase() === ELC_FUNDER_ID;

	return (
		<>
			{elcFunderId && (
				<div style={{ padding: '16px 24px 0' }}>
					<TabsContainer>
						<Tab selected={activeFunderId === null} onClick={() => selectFunderTab(null)} style={{ cursor: 'pointer' }}>
							<Text
								title="Insurance + DOE"
								type="psm"
								fontWeight={activeFunderId === null ? 'semiBold' : 'medium'}
							/>
						</Tab>
						<Tab
							selected={activeFunderId === elcFunderId}
							onClick={() => selectFunderTab(elcFunderId)}
							style={{ cursor: 'pointer' }}
						>
							<Text title="ELC" type="psm" fontWeight={activeFunderId === elcFunderId ? 'semiBold' : 'medium'} />
						</Tab>
					</TabsContainer>
				</div>
			)}
			<div className="skillAcquisitionHeaderWrapper">
				<div className="skillAcquisitionHeader">
					{!isBT && !isMobile && (
						<CustomButton
							title="Add Skill"
							onClick={() => setAddSkillAreaOpen(true)}
							buttonType="primary"
							startIcon={<Plus color={BACKGROUND.PRIMARY[100]} />}
						/>
					)}
					<CustomSwitch
						label="Show Archived"
						checked={includeArchived}
						onChange={showArchivedSkillAreasHandler}
						backgroundType="base"
						fullWidth={isMobile}
					/>
				</div>
				{!isBT && !isMobile && <UploadTargets funderId={activeFunderId} />}
			</div>
			<div className="skillAcquisitionContainer">
				{skillAreas?.map((item) => (
					<div className="skillAcquisitionAccordion" key={item.id}>
						<CustomAccordion
							HeaderContent={() => <ProgramsHeader item={item} isElc={isElcActive} />}
							Content={() => <ProgramsContent item={item} />}
							defaultExpanded={false}
							accordionClassName="clientInfoAccordion"
							accordionDetailsClassName="skillAcquisitionAccordionDetails"
						/>
					</div>
				))}
			</div>
			<CustomAlert
				open={addSkillAreaOpen}
				onClose={onCloseAddSkillArea}
				title={isElcActive ? 'Add Skill (ELC)' : 'Add Skill'}
				Content={({ onClose }) => <AddSkillContent onClose={onClose} funderId={activeFunderId} />}
			/>
		</>
	);
};

export default SkillAcquisition;
